/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useMemo, useEffect } from 'react';
import { 
  X, User, Mail, School, ShieldCheck, ShieldAlert, Key, Calendar, 
  Clock, Check, RefreshCw, Send, AlertTriangle, Building2, Globe, 
  Users, Copy, CheckCircle2, Crown, Sparkles, GraduationCap, Edit3, ArrowUpRight,
  Plus, Trash2, MapPin, CalendarDays, Edit2, CheckSquare, ChevronDown, ChevronUp, FileText, FileCheck2, Shield, Scale, Eye, Cake
} from 'lucide-react';
import { UserProfile, School as SchoolType, District as DistrictType, Tenant, UserRole } from '../../types';
import { StudentAvatar } from '../Common/StudentAvatar';
import { useInAppDialog } from '../Common/InAppDialog';
import { useWBL } from '../../context/WBLContext';
import { resolveDistrictForSchool } from '../../utils/schoolUtils';
import { formatRegionWithNoun } from '../../utils/regionUtils';
import { calculateAge, formatDateOfBirth } from '../../utils/timeUtils';

interface UserProfileModalProps {
  user: UserProfile | null;
  isOpen: boolean;
  onClose: () => void;
  onUpdateUser: (uid: string, updates: Partial<UserProfile>) => Promise<void>;
  onResetPassword: (user: UserProfile) => void;
  onAdminOverrideFerpa: (studentId: string) => void;
  onResendFerpa: (student: UserProfile, customEmail?: string) => Promise<void>;
  onReconcileStudent: (studentId: string) => Promise<void>;
  onSelectAnotherUser?: (user: UserProfile) => void;
  allSchools: SchoolType[];
  allDistricts: DistrictType[];
  allUsers: UserProfile[];
  tenants: Tenant[];
  isReconciling?: boolean;
}

export const UserProfileModal: React.FC<UserProfileModalProps> = ({
  user,
  isOpen,
  onClose,
  onUpdateUser,
  onResetPassword,
  onAdminOverrideFerpa,
  onResendFerpa,
  onReconcileStudent,
  onSelectAnotherUser,
  allSchools,
  allDistricts,
  allUsers,
  tenants,
  isReconciling = false
}) => {
  const { activeUser, logs, startProxySession } = useWBL();
  const { showToast } = useInAppDialog();
  const [copiedUid, setCopiedUid] = useState(false);
  const [savingField, setSavingField] = useState<string | null>(null);

  const isSuperadmin = activeUser?.role === 'superadmin' || activeUser?.email?.toLowerCase() === 'joshua@losonova.com';

  // Editable drafts
  const [tenantDraft, setTenantDraft] = useState(user?.tenantId || 'global');
  const [extendedRegionsDraft, setExtendedRegionsDraft] = useState<string[]>([]);
  const [selectedRegionToAdd, setSelectedRegionToAdd] = useState<string>('');
  const [roleDraft, setRoleDraft] = useState<string>(user?.role || 'student');
  const [districtDraft, setDistrictDraft] = useState(user?.district || '');
  const [schoolIdDraft, setSchoolIdDraft] = useState(user?.schoolId || '');
  const [schoolsServedDraft, setSchoolsServedDraft] = useState<string[]>([]);
  const [schoolsServedIdsDraft, setSchoolsServedIdsDraft] = useState<string[]>([]);
  const [selectedSchoolToAdd, setSelectedSchoolToAdd] = useState<string>('');

  // Additional schools (for students e.g. CTC / tech centers)
  const [additionalSchoolsDraft, setAdditionalSchoolsDraft] = useState<string[]>([]);
  const [additionalSchoolIdsDraft, setAdditionalSchoolIdsDraft] = useState<string[]>([]);
  const [selectedAdditionalSchoolToAdd, setSelectedAdditionalSchoolToAdd] = useState<string>('');

  const [dobDraft, setDobDraft] = useState(user?.dateOfBirth || '');
  const [recipientEmailDraft, setRecipientEmailDraft] = useState(user?.ferpaConsentRecipientEmail || user?.parentEmail || '');
  const [resending, setResending] = useState(false);
  const [showFerpaAuditDetails, setShowFerpaAuditDetails] = useState(false);

  // System audit dates
  const [isEditingAuditDates, setIsEditingAuditDates] = useState(false);
  const [createdAtDraft, setCreatedAtDraft] = useState(user?.createdAt || '');
  const [lastLoginDraft, setLastLoginDraft] = useState(user?.lastLogin || '');

  // Sync drafts when user changes
  useEffect(() => {
    if (user) {
      const primaryTenant = user.tenantId || 'global';
      setTenantDraft(primaryTenant);
      const ext = Array.isArray(user.extendedRegions) && user.extendedRegions.length > 0
        ? user.extendedRegions
        : [primaryTenant];
      const normalizedExt = Array.from(new Set([primaryTenant, ...ext])).filter(Boolean);
      setExtendedRegionsDraft(normalizedExt);
      setSelectedRegionToAdd('');
      setRoleDraft(user.role || 'student');
      setDistrictDraft(user.district || '');
      setSchoolIdDraft(user.schoolId || '');
      setSchoolsServedDraft(Array.isArray(user.schoolsServed) ? user.schoolsServed : []);
      setSchoolsServedIdsDraft(Array.isArray(user.schoolsServedIds) ? user.schoolsServedIds : []);
      setAdditionalSchoolsDraft(Array.isArray(user.additionalSchools) ? user.additionalSchools : []);
      setAdditionalSchoolIdsDraft(Array.isArray(user.additionalSchoolIds) ? user.additionalSchoolIds : []);
      setDobDraft(user.dateOfBirth || '');
      setRecipientEmailDraft(user.ferpaConsentRecipientEmail || user.parentEmail || '');
      setCreatedAtDraft(user.createdAt || '');
      setLastLoginDraft(user.lastLogin || '');
      setIsEditingAuditDates(false);
    }
  }, [user]);

  if (!isOpen || !user) return null;

  const uidStr = user.uid || (user as any).id || '';
  const roleStr = roleDraft || user.role || 'student';
  const isStudent = roleStr === 'student';
  const isParent = roleStr === 'parent';
  const isOrg = roleStr === 'organization';
  const isAdvisor = roleStr === 'advisor';
  const isStaffOrAdmin = isAdvisor || roleStr === 'regional_admin' || roleStr === 'superadmin' || roleStr === 'school_admin' || (!isStudent && !isParent && !isOrg);

  const age = calculateAge(dobDraft || user.dateOfBirth);
  const isUnder18 = age !== null ? age < 18 : null;


  const safeLower = (val: any) => (typeof val === 'string' ? val.toLowerCase().trim() : '');

  // Resolve user activity logs for dates
  const userEmailLower = safeLower(user.email);
  const userLogs = useMemo(() => {
    if (!logs || !userEmailLower) return [];
    return logs.filter(l => 
      (l.userEmail && safeLower(l.userEmail) === userEmailLower) ||
      (l.details && uidStr && l.details.includes(uidStr))
    );
  }, [logs, userEmailLower, uidStr]);

  const fallbackCreatedAt = useMemo(() => {
    if (createdAtDraft) return createdAtDraft;
    if (user.createdAt) return user.createdAt;
    if (userLogs.length > 0) {
      const sorted = [...userLogs].map(l => l.timestamp).filter(Boolean).sort();
      if (sorted.length > 0) return sorted[0];
    }
    return user.verifiedAt || user.ferpaConsentDate || user.advisorVerificationDate || '';
  }, [createdAtDraft, user, userLogs]);

  const fallbackLastLogin = useMemo(() => {
    if (lastLoginDraft) return lastLoginDraft;
    if (user.lastLogin) return user.lastLogin;
    if ((user as any).lastSignInTime) return (user as any).lastSignInTime;
    if (userLogs.length > 0) {
      const signins = userLogs.filter(l => l.action === 'USER_SIGN_IN').map(l => l.timestamp).filter(Boolean).sort();
      if (signins.length > 0) return signins[signins.length - 1];
      const sorted = [...userLogs].map(l => l.timestamp).filter(Boolean).sort();
      if (sorted.length > 0) return sorted[sorted.length - 1];
    }
    return user.updatedAt || user.verifiedAt || fallbackCreatedAt || '';
  }, [lastLoginDraft, user, userLogs, fallbackCreatedAt]);

  // Auto-backfill to user record if missing in document
  useEffect(() => {
    if (!user) return;
    const needCreated = !user.createdAt && fallbackCreatedAt;
    const needLogin = !user.lastLogin && fallbackLastLogin;
    if (needCreated || needLogin) {
      onUpdateUser(uidStr, {
        ...(needCreated ? { createdAt: fallbackCreatedAt } : {}),
        ...(needLogin ? { lastLogin: fallbackLastLogin } : {})
      }).catch(err => console.warn('Auto date sync warning:', err));
    }
  }, [user?.createdAt, user?.lastLogin, fallbackCreatedAt, fallbackLastLogin, uidStr, onUpdateUser]);

  // Available districts and schools filtered by tenantDraft
  const availableDistricts = useMemo(() => {
    if (!tenantDraft || tenantDraft === 'global') return allDistricts;
    return allDistricts.filter(d => 
      (d.regionId && d.regionId.toLowerCase() === tenantDraft.toLowerCase()) ||
      (d.tenantId && d.tenantId.toLowerCase() === tenantDraft.toLowerCase()) ||
      (d.name && d.name.toLowerCase().includes(tenantDraft.toLowerCase()))
    );
  }, [allDistricts, tenantDraft]);

  const availableSchools = useMemo(() => {
    if (!tenantDraft || tenantDraft === 'global') return allSchools;
    const allowedDistIds = new Set(availableDistricts.map(d => d.id));
    return allSchools.filter(s => {
      if (s.regionId && s.regionId.toLowerCase() === tenantDraft.toLowerCase()) return true;
      if (s.tenantId && s.tenantId.toLowerCase() === tenantDraft.toLowerCase()) return true;
      if (s.districtId && allowedDistIds.has(s.districtId)) return true;
      return false;
    });
  }, [allSchools, availableDistricts, tenantDraft]);

  // Unassigned schools in the region for Schools Served
  const unassignedSchoolsForServed = useMemo(() => {
    const assignedNames = new Set([
      ...(schoolIdDraft || user.school ? [safeLower(user.school || allSchools.find(s => s.id === schoolIdDraft)?.name)] : []),
      ...schoolsServedDraft.map(s => safeLower(s))
    ].filter(Boolean));
    const assignedIds = new Set([
      ...(schoolIdDraft ? [schoolIdDraft] : user.schoolId ? [user.schoolId] : []),
      ...schoolsServedIdsDraft
    ].filter(Boolean));
    return availableSchools.filter(s => !assignedNames.has(safeLower(s.name)) && !assignedIds.has(s.id));
  }, [availableSchools, schoolsServedDraft, schoolsServedIdsDraft, user.school, user.schoolId, schoolIdDraft, allSchools]);

  // Unassigned schools for student secondary / CTC
  const unassignedSchoolsForStudent = useMemo(() => {
    const assignedNames = new Set(additionalSchoolsDraft.map(s => safeLower(s)));
    const assignedIds = new Set(additionalSchoolIdsDraft);
    return availableSchools.filter(s => s.id !== schoolIdDraft && !assignedNames.has(safeLower(s.name)) && !assignedIds.has(s.id));
  }, [availableSchools, additionalSchoolsDraft, additionalSchoolIdsDraft, schoolIdDraft]);

  // Unassigned tenants for additional region access
  const unassignedTenants = useMemo(() => {
    return tenants.filter(t => t.id !== 'global' && !extendedRegionsDraft.includes(t.id));
  }, [tenants, extendedRegionsDraft]);

  // Linked parent(s) if user is student
  const linkedParents = (allUsers || []).filter(u => 
    u && u.role === 'parent' && (
      u.childId === uidStr ||
      (Array.isArray(u.childrenIds) && u.childrenIds.includes(uidStr)) ||
      (user.parentId && u.uid === user.parentId) ||
      (!!safeLower(u.email) && safeLower(u.email) === safeLower(user.parentEmail)) ||
      (!!safeLower(u.email) && safeLower(u.email) === safeLower(user.ferpaConsentRecipientEmail))
    )
  );

  const parentWithConsent = linkedParents.find(p => 
    typeof p.ferpaConsent === 'object' ? p.ferpaConsent?.granted === true : p.ferpaConsent === true
  );

  const directFerpaGranted = typeof user.ferpaConsent === 'object'
    ? user.ferpaConsent?.granted === true
    : user.ferpaConsent === true;

  const ferpaGranted = directFerpaGranted || !!parentWithConsent;
  const ferpaConsentDate = user.ferpaConsentDate || 
    (typeof user.ferpaConsent === 'object' ? user.ferpaConsent?.date : '') || 
    parentWithConsent?.ferpaConsentDate || 
    (typeof parentWithConsent?.ferpaConsent === 'object' ? parentWithConsent?.ferpaConsent?.date : '');
  const ferpaSignatory = user.ferpaConsentName || 
    (typeof user.ferpaConsent === 'object' ? user.ferpaConsent?.signatureValue : '') || 
    parentWithConsent?.ferpaConsentName || 
    parentWithConsent?.name || '';
  const ferpaSentAt = user.ferpaConsentSentAt;
  const ferpaRecipient = user.ferpaConsentRecipientEmail || user.parentEmail || parentWithConsent?.email || '';

  const studentAttestation = user.studentAttestation || (typeof user.ferpaConsent === 'object' ? user.ferpaConsent?.studentAttestation : undefined);
  const parentAttestation = user.parentAttestation || 
    (typeof user.ferpaConsent === 'object' ? user.ferpaConsent?.parentAttestation : undefined) ||
    parentWithConsent?.parentAttestation || 
    (typeof parentWithConsent?.ferpaConsent === 'object' ? parentWithConsent?.ferpaConsent?.parentAttestation : undefined);

  // Filter logs specifically for FERPA attestation and consent events related to this user/family
  const ferpaAuditLogs = useMemo(() => {
    if (!logs) return [];
    const sId = uidStr ? uidStr.toLowerCase() : '';
    const sEmail = safeLower(user.email);
    const sName = user.name ? user.name.toLowerCase() : '';
    const pEmail = safeLower(user.parentEmail || user.ferpaConsentRecipientEmail);
    const linkedParentEmails = linkedParents.map(p => safeLower(p.email)).filter(Boolean);

    return logs.filter(l => {
      const act = l.action || '';
      const details = (l.details || '').toLowerCase();
      const logEmail = safeLower(l.userEmail);

      const isFerpaAction = act.includes('FERPA') || details.includes('ferpa') || act === 'RESEND_FERPA_CONSENT';
      if (!isFerpaAction) return false;

      const matchesStudent = (sEmail && (logEmail === sEmail || details.includes(sEmail))) ||
        (sId && details.includes(sId)) ||
        (sName && details.includes(sName));

      const matchesParent = (pEmail && (logEmail === pEmail || details.includes(pEmail))) ||
        linkedParentEmails.some(lpe => logEmail === lpe || details.includes(lpe));

      return matchesStudent || matchesParent;
    });
  }, [logs, user, uidStr, linkedParents]);

  // Auto-heal student FERPA record in Firestore if parent granted consent
  useEffect(() => {
    if (isStudent && !directFerpaGranted && parentWithConsent && uidStr) {
      const pName = parentWithConsent.name || parentWithConsent.ferpaConsentName || 'Parent / Legal Guardian';
      const cDate = parentWithConsent.ferpaConsentDate || (typeof parentWithConsent.ferpaConsent === 'object' ? parentWithConsent.ferpaConsent?.date : '') || new Date().toISOString();
      onUpdateUser(uidStr, {
        ferpaConsent: {
          granted: true,
          date: cDate,
          version: '1.0',
          signatureType: 'type',
          signatureValue: pName
        },
        ferpaConsentName: pName,
        ferpaConsentDate: cDate,
        parentId: parentWithConsent.uid,
        parentEmail: parentWithConsent.email ? parentWithConsent.email.toLowerCase().trim() : user.parentEmail
      }).catch(err => console.warn('Auto student FERPA sync note:', err));
    }
  }, [isStudent, directFerpaGranted, parentWithConsent, uidStr]);

  // Linked student(s) if user is parent
  const linkedStudents = (allUsers || []).filter(u =>
    u && u.role === 'student' && (
      u.parentId === uidStr ||
      u.childId === uidStr ||
      (Array.isArray(u.childrenIds) && u.childrenIds.includes(uidStr)) ||
      (user.childId && u.uid === user.childId) ||
      (Array.isArray(user.childrenIds) && user.childrenIds.includes(u.uid)) ||
      (!!safeLower(user.email) && safeLower(user.email) === safeLower(u.parentEmail)) ||
      (!!safeLower(user.email) && safeLower(user.email) === safeLower(u.ferpaConsentRecipientEmail))
    )
  );

  // Assigned advisors for student's school
  const assignedAdvisors = (allUsers || []).filter(u => {
    if (!u || u.role !== 'advisor') return false;
    const advSchoolRefs = [
      u.schoolId,
      u.school,
      u.signupSchoolId,
      u.signupSchool,
      ...(u.schoolsServedIds || []),
      ...(u.schoolsServed || [])
    ].filter(Boolean).map(s => String(s).toLowerCase().trim().replace(/\s*\(.*\)$/, ''));

    const studentSchoolRefs = [
      schoolIdDraft,
      user.schoolId,
      user.school,
      ...(user.additionalSchoolIds || []),
      ...(user.additionalSchools || [])
    ].filter(Boolean).map(s => String(s).toLowerCase().trim().replace(/\s*\(.*\)$/, ''));

    return studentSchoolRefs.some(ssr => advSchoolRefs.some(asr => asr === ssr || asr.includes(ssr) || ssr.includes(asr)));
  });

  const formatDateTime = (iso?: string | null) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      if (isNaN(d.getTime())) return iso;
      return d.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZone: 'America/New_York'
      }) + ' EST';
    } catch {
      return iso || '—';
    }
  };

  const toDatetimeLocalValue = (iso?: string | null) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      if (isNaN(d.getTime())) return '';
      const pad = (n: number) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    } catch {
      return '';
    }
  };

  const handleCopyUid = () => {
    if (uidStr) {
      navigator.clipboard.writeText(uidStr);
      setCopiedUid(true);
      setTimeout(() => setCopiedUid(false), 2000);
    }
  };

  const handleTenantChange = async (newTenantId: string) => {
    if (user?.role === 'student' && newTenantId === 'global') {
      showToast({ 
        title: 'Action Prohibited', 
        message: 'Students cannot be assigned to the Global region. Students must be affiliated with a specific county/district region.', 
        type: 'error' 
      });
      return;
    }
    setTenantDraft(newTenantId);
    setSavingField('region');
    const matchedTenant = tenants.find(t => t.id === newTenantId);
    try {
      const updatedExtended = Array.from(new Set([newTenantId, ...extendedRegionsDraft]));
      setExtendedRegionsDraft(updatedExtended);
      await onUpdateUser(uidStr, {
        tenantId: newTenantId,
        region: newTenantId === 'global' ? 'global' : (matchedTenant?.regionName || newTenantId),
        extendedRegions: updatedExtended
      });
      showToast({ 
        title: 'Primary Region Updated', 
        message: `Assigned primary region changed to ${matchedTenant?.regionName || matchedTenant?.name || newTenantId.toUpperCase()}.`, 
        type: 'success' 
      });
    } catch (err: any) {
      showToast({ title: 'Update Failed', message: err.message, type: 'error' });
    } finally {
      setSavingField(null);
    }
  };

  const handleAddExtendedRegion = async (tenantIdToAdd: string) => {
    if (!tenantIdToAdd) return;
    if (extendedRegionsDraft.includes(tenantIdToAdd)) return;

    const nextRegions = Array.from(new Set([...extendedRegionsDraft, tenantIdToAdd]));
    setExtendedRegionsDraft(nextRegions);
    setSelectedRegionToAdd('');

    const matchedTenant = tenants.find(t => t.id === tenantIdToAdd);
    const regionLabel = matchedTenant?.regionName || matchedTenant?.name || tenantIdToAdd.toUpperCase();

    try {
      await onUpdateUser(uidStr, { extendedRegions: nextRegions });
      showToast({ title: 'Region Added', message: `Added access to ${regionLabel}.`, type: 'success' });
    } catch (err: any) {
      showToast({ title: 'Update Failed', message: err.message, type: 'error' });
    }
  };

  const handleRemoveExtendedRegion = async (tenantIdToRemove: string) => {
    if (extendedRegionsDraft.length <= 1) {
      showToast({ title: 'Region Required', message: 'A user must be assigned to at least one active region.', type: 'warning' });
      return;
    }

    const nextRegions = extendedRegionsDraft.filter(r => r !== tenantIdToRemove);
    if (nextRegions.length === 0) {
      showToast({ title: 'Region Required', message: 'A user must be assigned to at least one active region.', type: 'warning' });
      return;
    }

    let nextTenant = tenantDraft;
    let updates: Partial<UserProfile> = { extendedRegions: nextRegions };

    if (tenantIdToRemove === tenantDraft) {
      nextTenant = nextRegions[0];
      setTenantDraft(nextTenant);
      const matchedTenant = tenants.find(t => t.id === nextTenant);
      updates.tenantId = nextTenant;
      updates.region = nextTenant === 'global' ? 'global' : (matchedTenant?.regionName || nextTenant);
    }

    setExtendedRegionsDraft(nextRegions);

    const matchedTenant = tenants.find(t => t.id === tenantIdToRemove);
    const regionLabel = matchedTenant?.regionName || matchedTenant?.name || tenantIdToRemove.toUpperCase();

    try {
      await onUpdateUser(uidStr, updates);
      showToast({ title: 'Region Access Removed', message: `Removed access to ${regionLabel}.`, type: 'success' });
    } catch (err: any) {
      showToast({ title: 'Update Failed', message: err.message, type: 'error' });
    }
  };

  const handleRoleChange = async (newRole: any) => {
    setRoleDraft(newRole);
    setSavingField('role');
    try {
      await onUpdateUser(uidStr, { role: newRole });
      showToast({ title: 'Role Updated', message: `User system role changed to ${newRole.toUpperCase()}.`, type: 'success' });
    } catch (err: any) {
      showToast({ title: 'Update Failed', message: err.message, type: 'error' });
    } finally {
      setSavingField(null);
    }
  };

  const handlePrimarySchoolChange = async (newSchoolId: string) => {
    setSchoolIdDraft(newSchoolId);
    setSavingField('affiliation');
    try {
      const matchedSchool = allSchools.find(s => s.id === newSchoolId);
      const newSchoolName = matchedSchool ? matchedSchool.name : '';
      
      const resolved = resolveDistrictForSchool(newSchoolName || newSchoolId, undefined, allSchools, allDistricts);
      let newDistrict = districtDraft;
      let newDistrictId = user.districtId || '';
      if (resolved.districtName) {
        newDistrict = resolved.districtName;
        newDistrictId = resolved.districtId;
        setDistrictDraft(resolved.districtName);
      }

      const schoolRegion = resolved.regionId || matchedSchool?.regionId;
      const updatesPayload: any = {
        schoolId: newSchoolId,
        school: newSchoolName,
        pendingSchoolId: newSchoolId,
        pendingSchool: newSchoolName,
        district: newDistrict,
        districtId: newDistrictId
      };

      if (schoolRegion && schoolRegion !== 'global' && (user.role === 'student' || tenantDraft === 'global' || !user.tenantId)) {
        updatesPayload.tenantId = schoolRegion;
        updatesPayload.region = formatRegionWithNoun(schoolRegion);
        setTenantDraft(schoolRegion);
        const updatedExtended = Array.from(new Set([schoolRegion, ...extendedRegionsDraft.filter(r => r !== 'global')]));
        updatesPayload.extendedRegions = updatedExtended;
        setExtendedRegionsDraft(updatedExtended);
      }

      await onUpdateUser(uidStr, updatesPayload);

      if (newSchoolName) {
        showToast({ title: 'Primary School Set', message: `Assigned ${newSchoolName} as primary high school.`, type: 'success' });
      } else {
        showToast({ title: 'Primary School Removed', message: 'Primary high school assignment cleared.', type: 'success' });
      }
    } catch (err: any) {
      showToast({ title: 'Update Failed', message: err.message, type: 'error' });
    } finally {
      setSavingField(null);
    }
  };

  const handleRemovePrimarySchool = async () => {
    const prevSchool = user.school || allSchools.find(s => s.id === schoolIdDraft)?.name || '';
    setSchoolIdDraft('');
    const nextServed = schoolsServedDraft.filter(s => s !== prevSchool);
    const nextServedIds = schoolsServedIdsDraft.filter(id => id !== user.schoolId && id !== schoolIdDraft);
    setSchoolsServedDraft(nextServed);
    setSchoolsServedIdsDraft(nextServedIds);

    try {
      await onUpdateUser(uidStr, {
        school: '',
        schoolId: '',
        pendingSchool: '',
        pendingSchoolId: '',
        schoolsServed: nextServed,
        schoolsServedIds: nextServedIds
      });
      showToast({ title: 'School Removed', message: 'Primary high school assignment removed.', type: 'success' });
    } catch (err: any) {
      showToast({ title: 'Update Failed', message: err.message, type: 'error' });
    }
  };

  const handleSaveAffiliation = async () => {
    setSavingField('affiliation');
    try {
      const matchedSchool = allSchools.find(s => s.id === schoolIdDraft);
      const matchedDistrict = allDistricts.find(d => d.name === districtDraft || d.id === districtDraft);
      await onUpdateUser(uidStr, {
        district: matchedDistrict?.name || districtDraft,
        districtId: matchedDistrict?.id || user.districtId || '',
        schoolId: schoolIdDraft,
        school: matchedSchool ? matchedSchool.name : ''
      });
      showToast({ title: 'Affiliation Saved', message: 'School & district assignments updated.', type: 'success' });
    } catch (err: any) {
      showToast({ title: 'Update Failed', message: err.message, type: 'error' });
    } finally {
      setSavingField(null);
    }
  };

  const handleAddSchoolServed = async (schoolIdToAdd: string) => {
    if (!schoolIdToAdd) return;
    const targetSchool = allSchools.find(s => s.id === schoolIdToAdd);
    if (!targetSchool) return;

    const schoolName = targetSchool.name;
    const nextServed = Array.from(new Set([...schoolsServedDraft, schoolName]));
    const nextServedIds = Array.from(new Set([...schoolsServedIdsDraft, schoolIdToAdd]));

    setSchoolsServedDraft(nextServed);
    setSchoolsServedIdsDraft(nextServedIds);
    setSelectedSchoolToAdd('');

    try {
      await onUpdateUser(uidStr, {
        schoolsServed: nextServed,
        schoolsServedIds: nextServedIds
      });
      showToast({ title: 'School Assigned', message: `Added ${schoolName} to schools served.`, type: 'success' });
    } catch (err: any) {
      showToast({ title: 'Update Failed', message: err.message, type: 'error' });
    }
  };

  const handleRemoveSchoolServed = async (schoolNameToRemove: string) => {
    const nextServed = schoolsServedDraft.filter(s => s !== schoolNameToRemove);
    const targetSchool = allSchools.find(s => s.name === schoolNameToRemove);
    const nextServedIds = targetSchool
      ? schoolsServedIdsDraft.filter(id => id !== targetSchool.id)
      : schoolsServedIdsDraft;

    setSchoolsServedDraft(nextServed);
    setSchoolsServedIdsDraft(nextServedIds);

    const updates: Partial<UserProfile> = {
      schoolsServed: nextServed,
      schoolsServedIds: nextServedIds
    };

    if (user.school === schoolNameToRemove || (targetSchool && user.schoolId === targetSchool.id)) {
      setSchoolIdDraft('');
      updates.school = '';
      updates.schoolId = '';
      updates.pendingSchool = '';
      updates.pendingSchoolId = '';
    }

    try {
      await onUpdateUser(uidStr, updates);
      showToast({ title: 'School Removed', message: `Removed ${schoolNameToRemove} from assignments.`, type: 'success' });
    } catch (err: any) {
      showToast({ title: 'Update Failed', message: err.message, type: 'error' });
    }
  };

  const handleAddAdditionalSchool = async (schoolIdToAdd: string) => {
    if (!schoolIdToAdd) return;
    const targetSchool = allSchools.find(s => s.id === schoolIdToAdd);
    if (!targetSchool) return;

    const schoolName = targetSchool.name;
    const nextList = Array.from(new Set([...additionalSchoolsDraft, schoolName]));
    const nextIds = Array.from(new Set([...additionalSchoolIdsDraft, schoolIdToAdd]));

    setAdditionalSchoolsDraft(nextList);
    setAdditionalSchoolIdsDraft(nextIds);
    setSelectedAdditionalSchoolToAdd('');

    try {
      await onUpdateUser(uidStr, {
        additionalSchools: nextList,
        additionalSchoolIds: nextIds
      });
      showToast({ title: 'Campus Added', message: `Added ${schoolName} to secondary/CTC schools.`, type: 'success' });
    } catch (err: any) {
      showToast({ title: 'Update Failed', message: err.message, type: 'error' });
    }
  };

  const handleRemoveAdditionalSchool = async (schoolNameToRemove: string) => {
    const nextList = additionalSchoolsDraft.filter(s => s !== schoolNameToRemove);
    const targetSchool = allSchools.find(s => s.name === schoolNameToRemove);
    const nextIds = targetSchool
      ? additionalSchoolIdsDraft.filter(id => id !== targetSchool.id)
      : additionalSchoolIdsDraft;

    setAdditionalSchoolsDraft(nextList);
    setAdditionalSchoolIdsDraft(nextIds);

    try {
      await onUpdateUser(uidStr, {
        additionalSchools: nextList,
        additionalSchoolIds: nextIds
      });
      showToast({ title: 'Campus Removed', message: `Removed ${schoolNameToRemove}.`, type: 'success' });
    } catch (err: any) {
      showToast({ title: 'Update Failed', message: err.message, type: 'error' });
    }
  };

  const handleSaveAuditDate = async (field: 'createdAt' | 'lastLogin', value: string) => {
    if (!value) return;
    try {
      const isoString = new Date(value).toISOString();
      if (field === 'createdAt') {
        setCreatedAtDraft(isoString);
        await onUpdateUser(uidStr, { createdAt: isoString });
        showToast({ title: 'Date Created Saved', message: 'Account creation timestamp updated.', type: 'success' });
      } else {
        setLastLoginDraft(isoString);
        await onUpdateUser(uidStr, { lastLogin: isoString });
        showToast({ title: 'Last Login Saved', message: 'Last login / active timestamp updated.', type: 'success' });
      }
    } catch (err: any) {
      showToast({ title: 'Date Format Error', message: 'Please select a valid date/time.', type: 'error' });
    }
  };

  const handleSaveDob = async () => {
    setSavingField('dob');
    try {
      await onUpdateUser(uidStr, { dateOfBirth: dobDraft });
      showToast({ title: 'Date of Birth Saved', message: 'Student age updated.', type: 'success' });
    } finally {
      setSavingField(null);
    }
  };

  const handleSaveRecipientEmail = async () => {
    setSavingField('recipientEmail');
    try {
      await onUpdateUser(uidStr, {
        ferpaConsentRecipientEmail: recipientEmailDraft.trim().toLowerCase(),
        parentEmail: recipientEmailDraft.trim().toLowerCase()
      });
      showToast({ title: 'Parent Email Saved', message: 'FERPA recipient email updated.', type: 'success' });
    } finally {
      setSavingField(null);
    }
  };

  const handleTriggerResend = async () => {
    setResending(true);
    try {
      await onResendFerpa(user, recipientEmailDraft.trim().toLowerCase());
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs animate-fade-in">
      <div className="bg-white rounded-2xl border border-slate-200 shadow-2xl w-full max-w-xl max-h-[92vh] flex flex-col overflow-hidden animate-scale-up">
        {/* Compact Header */}
        <div className="px-5 py-4 border-b border-slate-100 bg-slate-50/80 flex items-center justify-between gap-3">
          <div className="flex items-center space-x-3 min-w-0">
            <div className="w-10 h-10 rounded-xl overflow-hidden bg-white border border-slate-200 shrink-0 shadow-3xs">
              <StudentAvatar studentId={uidStr} studentName={user.name} studentEmail={user.email} size="sm" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h2 className="text-base font-black text-[#011d3c] tracking-tight truncate">
                  {user.name || 'Unnamed Account'}
                </h2>
                <span className={`px-2 py-0.5 rounded text-[8px] font-black uppercase tracking-wider border ${
                  roleStr === 'student' ? 'bg-sky-50 text-sky-700 border-sky-200' :
                  roleStr === 'advisor' ? 'bg-indigo-50 text-indigo-700 border-indigo-200' :
                  roleStr === 'parent' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                  roleStr === 'organization' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                  roleStr === 'regional_admin' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                  'bg-purple-50 text-purple-700 border-purple-200'
                }`}>
                  {roleStr === 'organization' ? (user.isOrgLead ? 'Employer (Lead)' : 'Employer') : roleStr.replace('_', ' ')}
                </span>
                <span className="px-1.5 py-0.5 rounded text-[8px] font-bold text-slate-500 bg-slate-100 uppercase">
                  {tenantDraft || user.tenantId || 'global'}
                </span>
              </div>
              <div className="text-[11px] text-slate-400 truncate">{user.email}</div>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-200/50 rounded-lg transition-colors cursor-pointer"
            title="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Quick Action Toolbar */}
        <div className="px-5 py-2.5 bg-white border-b border-slate-100 flex flex-wrap items-center justify-between gap-2 text-xs">
          <div className="flex flex-wrap items-center gap-1.5">
            {/* Verification Toggle */}
            <button
              type="button"
              onClick={() => onUpdateUser(uidStr, { verified: !user.verified })}
              className={`inline-flex items-center space-x-1 px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider transition-all cursor-pointer border ${
                user.verified 
                  ? 'bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-100' 
                  : 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100'
              }`}
            >
              <CheckCircle2 className="w-3 h-3" />
              <span>{user.verified ? 'Revoke Verification' : 'Verify Account'}</span>
            </button>

            {/* Password Reset */}
            <button
              type="button"
              onClick={() => onResetPassword(user)}
              className="inline-flex items-center space-x-1 px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider bg-slate-100 hover:bg-slate-200 text-slate-700 transition-all cursor-pointer"
            >
              <Key className="w-3 h-3" />
              <span>Reset Password</span>
            </button>

            {/* Superadmin Remote Proxy (Mimic View) */}
            {isSuperadmin && user.uid !== activeUser?.uid && (
              <button
                type="button"
                onClick={() => {
                  onClose();
                  startProxySession(user);
                }}
                className="inline-flex items-center space-x-1 px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider bg-amber-500/10 text-amber-800 dark:text-amber-300 border border-amber-500/30 hover:bg-amber-500/20 transition-all cursor-pointer"
                title={`Discreetly mimic ${user.name || user.email}'s view as a remote proxy`}
              >
                <Eye className="w-3 h-3 text-amber-600 dark:text-amber-400" />
                <span>Mimic View</span>
              </button>
            )}

            {/* Org Lead Toggle */}
            {isOrg && (
              <button
                type="button"
                onClick={() => onUpdateUser(uidStr, { isOrgLead: !user.isOrgLead, orgRole: user.isOrgLead ? 'admin' : 'owner' })}
                className={`inline-flex items-center space-x-1 px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider transition-all cursor-pointer border ${
                  user.isOrgLead 
                    ? 'bg-amber-100 text-amber-800 border-amber-300' 
                    : 'bg-slate-100 text-slate-700 border-slate-200 hover:bg-slate-200'
                }`}
              >
                <Crown className="w-3 h-3" />
                <span>{user.isOrgLead ? 'Lead ✓' : 'Make Lead'}</span>
              </button>
            )}
          </div>

          {/* Student FERPA quick actions */}
          {isStudent && (
            <div className="flex items-center gap-1.5">
              {!ferpaGranted && (
                <button
                  type="button"
                  disabled={isReconciling}
                  onClick={() => onReconcileStudent(uidStr)}
                  title="Check and match FERPA records in Firestore"
                  className="inline-flex items-center space-x-1 px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider bg-emerald-600 hover:bg-emerald-700 text-white transition-all cursor-pointer shadow-3xs"
                >
                  {isReconciling ? <RefreshCw className="w-3 h-3 animate-spin" /> : <ShieldCheck className="w-3 h-3" />}
                  <span>Sync FERPA</span>
                </button>
              )}
              <button
                type="button"
                onClick={() => onAdminOverrideFerpa(uidStr)}
                className="inline-flex items-center space-x-1 px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider bg-sky-50 text-sky-700 border border-sky-200 hover:bg-sky-100 transition-all cursor-pointer"
              >
                <ShieldAlert className="w-3 h-3" />
                <span>Override</span>
              </button>
            </div>
          )}
        </div>

        {/* Property List Body */}
        <div className="overflow-y-auto flex-1 divide-y divide-slate-100 text-xs">
          
          {/* Section: Account & Identity */}
          <div>
            <div className="px-5 py-1.5 bg-slate-50 text-[10px] font-black uppercase tracking-wider text-slate-400">
              Account & Identity
            </div>
            <div className="divide-y divide-slate-100">
              <div className="flex items-center justify-between px-5 py-2 hover:bg-slate-50/50">
                <span className="text-slate-500 font-medium">User ID</span>
                <button
                  type="button"
                  onClick={handleCopyUid}
                  className="font-mono text-[11px] text-slate-700 hover:text-[#0094c8] flex items-center gap-1 cursor-pointer"
                  title="Click to copy full UID"
                >
                  <span>{uidStr.slice(0, 16)}...</span>
                  {copiedUid ? <Check className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3 text-slate-400" />}
                </button>
              </div>

              <div className="flex items-center justify-between px-5 py-2 hover:bg-slate-50/50">
                <span className="text-slate-500 font-medium">Email Address</span>
                <span className="font-semibold text-slate-800">{user.email}</span>
              </div>

              {/* System Role (Editable by Superadmin) */}
              <div className="flex items-center justify-between px-5 py-2 hover:bg-slate-50/50">
                <span className="text-slate-500 font-medium">System Role</span>
                {isSuperadmin ? (
                  <select
                    value={roleDraft}
                    onChange={(e) => handleRoleChange(e.target.value)}
                    className="text-xs px-2 py-1 bg-white border border-slate-300 rounded font-bold uppercase text-slate-800 focus:ring-1 focus:ring-[#0094c8] outline-none"
                  >
                    <option value="student">Student</option>
                    <option value="advisor">Advisor</option>
                    <option value="regional_admin">Regional Admin</option>
                    <option value="school_admin">School Admin</option>
                    <option value="organization">Organization (Employer)</option>
                    <option value="parent">Parent</option>
                    <option value="superadmin">Superadmin</option>
                  </select>
                ) : (
                  <span className="font-bold uppercase text-slate-700">{roleStr}</span>
                )}
              </div>

              {/* Region / Portal (Editable by Superadmin & Admins) */}
              <div className="flex items-center justify-between px-5 py-2 hover:bg-slate-50/50">
                <div>
                  <span className="text-slate-500 font-medium block">Primary Region / Portal</span>
                  {savingField === 'region' && (
                    <span className="text-[9px] text-[#0094c8] font-semibold animate-pulse">Saving region...</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <select
                    value={tenantDraft}
                    onChange={(e) => handleTenantChange(e.target.value)}
                    className="text-xs px-2.5 py-1 bg-white border border-slate-300 rounded-lg font-bold text-slate-800 focus:ring-2 focus:ring-[#0094c8] outline-none uppercase shadow-3xs cursor-pointer"
                  >
                    {tenants.map(t => (
                      <option key={t.id} value={t.id}>
                        {t.regionName || t.name || t.id.toUpperCase()} ({t.id})
                      </option>
                    ))}
                    {!isStudent && <option value="global">GLOBAL / ALL REGIONS</option>}
                  </select>
                </div>
              </div>

              {/* Extended / Additional Assigned Regions */}
              <div className="px-5 py-3 bg-slate-50/50 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-slate-600 font-bold text-xs flex items-center gap-1.5">
                    <Globe className="w-3.5 h-3.5 text-[#0094c8]" />
                    <span>Assigned Regions & Access ({extendedRegionsDraft.length})</span>
                  </span>
                  <span className="text-[10px] text-slate-400 uppercase font-mono">Multi-Region Access</span>
                </div>

                {/* Active Region Badges with Remove Buttons */}
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {extendedRegionsDraft.map((regId) => {
                    const matchedTenant = tenants.find(t => t.id === regId);
                    const label = regId === 'global' ? 'Global / All Regions' : (matchedTenant?.regionName || matchedTenant?.name || regId.toUpperCase());
                    const isPrimary = regId === tenantDraft;

                    return (
                      <span
                        key={regId}
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold border shadow-3xs ${
                          isPrimary
                            ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
                            : 'bg-sky-50 text-sky-800 border-sky-200'
                        }`}
                      >
                        {isPrimary && (
                          <span className="text-[9px] uppercase font-black text-emerald-600 bg-emerald-100 px-1.5 py-0.5 rounded">
                            Primary
                          </span>
                        )}
                        <span>{label}</span>
                        {extendedRegionsDraft.length > 1 && (
                          <button
                            type="button"
                            onClick={() => handleRemoveExtendedRegion(regId)}
                            className="text-slate-400 hover:text-rose-600 hover:bg-rose-100/80 rounded p-0.5 transition-colors cursor-pointer"
                            title={`Remove access to ${label}`}
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </span>
                    );
                  })}
                </div>

                {/* Dropdown to assign additional region */}
                <div className="pt-2 flex items-center gap-2">
                  <select
                    value={selectedRegionToAdd}
                    onChange={(e) => {
                      const regId = e.target.value;
                      setSelectedRegionToAdd(regId);
                      if (regId) handleAddExtendedRegion(regId);
                    }}
                    className="flex-1 text-xs px-2.5 py-1.5 bg-white border border-slate-200 rounded-lg font-medium text-slate-700 outline-none focus:ring-1 focus:ring-[#0094c8] cursor-pointer"
                  >
                    <option value="">+ Assign additional region access to user...</option>
                    {!extendedRegionsDraft.includes('global') && (
                      <option value="global">GLOBAL / ALL REGIONS (global)</option>
                    )}
                    {unassignedTenants.map(t => (
                      <option key={t.id} value={t.id}>
                        {t.regionName || t.name || t.id.toUpperCase()} ({t.id})
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex items-center justify-between px-5 py-2 hover:bg-slate-50/50">
                <span className="text-slate-500 font-medium">Verification Status</span>
                {user.verified ? (
                  <span className="inline-flex items-center gap-1 text-emerald-700 font-bold">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                    Verified
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-slate-400 font-bold">
                    <Clock className="w-3.5 h-3.5 text-slate-400" />
                    Pending Verification
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Section: Affiliation & Schooling */}
          <div>
            <div className="px-5 py-1.5 bg-slate-50 text-[10px] font-black uppercase tracking-wider text-slate-400">
              {isOrg ? 'Employer Affiliation' : 'Affiliation & Schooling'}
            </div>
            <div className="divide-y divide-slate-100">
              {isOrg ? (
                <>
                  <div className="flex items-center justify-between px-5 py-2 hover:bg-slate-50/50">
                    <span className="text-slate-500 font-medium">Organization / Company</span>
                    <input
                      type="text"
                      defaultValue={user.orgName || ''}
                      onBlur={(e) => onUpdateUser(uidStr, { orgName: e.target.value })}
                      placeholder="Organization Name"
                      className="text-xs px-2 py-1 bg-white border border-slate-200 rounded font-semibold text-slate-800 text-right focus:ring-1 focus:ring-[#0094c8] outline-none"
                    />
                  </div>
                  <div className="flex items-center justify-between px-5 py-2 hover:bg-slate-50/50">
                    <span className="text-slate-500 font-medium">Organization Role</span>
                    <span className="font-semibold text-slate-700">{user.isOrgLead ? 'Designated Lead' : 'Team Member'}</span>
                  </div>
                  <div className="flex items-center justify-between px-5 py-3 hover:bg-slate-50/50">
                    <div>
                      <span className="text-slate-700 font-bold block text-xs">Superadmin Appearance Oversight</span>
                      <span className="text-[11px] text-slate-400 font-medium">Show in Participating Organizations carousel</span>
                    </div>
                    <input
                      type="checkbox"
                      defaultChecked={user.showInParticipatingOrgs || false}
                      onChange={(e) => onUpdateUser(uidStr, { showInParticipatingOrgs: e.target.checked })}
                      className="w-4 h-4 rounded text-[#0094c8] focus:ring-[#0094c8] border-slate-300 cursor-pointer"
                    />
                  </div>
                </>
              ) : (
                <>
                  {/* School District */}
                  <div className="flex items-center justify-between px-5 py-2 hover:bg-slate-50/50">
                    <span className="text-slate-500 font-medium">School District</span>
                    <select
                      value={districtDraft}
                      onChange={(e) => {
                        setDistrictDraft(e.target.value);
                      }}
                      onBlur={handleSaveAffiliation}
                      className="text-xs px-2 py-1 bg-white border border-slate-200 rounded font-semibold text-slate-800 max-w-[250px] focus:ring-1 focus:ring-[#0094c8] outline-none cursor-pointer"
                    >
                      <option value="">Unassigned</option>
                      {availableDistricts.map(d => (
                        <option key={d.id} value={d.name}>{d.name}</option>
                      ))}
                    </select>
                  </div>

                  {/* Primary High School */}
                  <div className="flex items-center justify-between px-5 py-2 hover:bg-slate-50/50">
                    <span className="text-slate-500 font-medium">
                      {isStaffOrAdmin ? 'Primary Assigned High School' : 'Assigned High School'}
                    </span>
                    <div className="flex items-center gap-1.5">
                      <select
                        value={schoolIdDraft}
                        onChange={(e) => handlePrimarySchoolChange(e.target.value)}
                        className="text-xs px-2 py-1 bg-white border border-slate-200 rounded font-semibold text-slate-800 max-w-[220px] focus:ring-1 focus:ring-[#0094c8] outline-none cursor-pointer"
                      >
                        <option value="">Unassigned (None)</option>
                        {availableSchools.map(s => (
                          <option key={s.id} value={s.id}>{s.name}</option>
                        ))}
                      </select>
                      {(schoolIdDraft || user.school) && (
                        <button
                          type="button"
                          onClick={handleRemovePrimarySchool}
                          className="p-1 rounded text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors cursor-pointer"
                          title="Remove / Clear Primary High School"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Schools Served Multi-Campus Assignment (For Advisors, Admins, Staff) */}
                  {isStaffOrAdmin && (
                    <div className="px-5 py-3 bg-slate-50/50 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-slate-600 font-bold text-xs flex items-center gap-1.5">
                          <School className="w-3.5 h-3.5 text-[#0094c8]" />
                          <span>
                            Schools Served ({schoolsServedDraft.filter(s => s !== user.school).length + ((user.school || schoolIdDraft) ? 1 : 0)})
                          </span>
                        </span>
                        <span className="text-[10px] text-slate-400 uppercase font-mono">Multi-School Coverage</span>
                      </div>

                      {/* Active Badges with Removal Buttons */}
                      <div className="flex flex-wrap gap-1.5 pt-1">
                        {/* Primary school badge with X button */}
                        {(user.school || (schoolIdDraft && allSchools.find(s => s.id === schoolIdDraft)?.name)) && (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold bg-emerald-50 text-emerald-800 border border-emerald-200 shadow-3xs group">
                            <span className="text-[9px] uppercase font-black text-emerald-600 bg-emerald-100/70 px-1.5 py-0.5 rounded">Primary</span>
                            <span>{user.school || allSchools.find(s => s.id === schoolIdDraft)?.name}</span>
                            <button
                              type="button"
                              onClick={handleRemovePrimarySchool}
                              className="text-emerald-700/60 hover:text-rose-600 hover:bg-rose-100/80 rounded p-0.5 transition-colors cursor-pointer"
                              title={`Remove ${user.school || 'primary school'} assignment`}
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </span>
                        )}

                        {/* Additional schools served with X button */}
                        {schoolsServedDraft
                          .filter(s => s !== user.school && (!schoolIdDraft || s !== allSchools.find(sch => sch.id === schoolIdDraft)?.name))
                          .map((schoolName, idx) => (
                            <span
                              key={idx}
                              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold bg-[#0094c8]/10 text-[#007ba6] border border-[#0094c8]/25 shadow-3xs"
                            >
                              <span>{schoolName}</span>
                              <button
                                type="button"
                                onClick={() => handleRemoveSchoolServed(schoolName)}
                                className="text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded p-0.5 transition-colors cursor-pointer"
                                title={`Remove ${schoolName} from coverage`}
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </span>
                          ))}

                        {!user.school && !schoolIdDraft && schoolsServedDraft.length === 0 && (
                          <span className="text-slate-400 italic text-[11px]">
                            No schools currently assigned to this staff member.
                          </span>
                        )}
                      </div>

                      {/* Dropdown to assign more schools */}
                      <div className="pt-2 flex items-center gap-2">
                        <select
                          value={selectedSchoolToAdd}
                          onChange={(e) => {
                            const scId = e.target.value;
                            setSelectedSchoolToAdd(scId);
                            if (scId) handleAddSchoolServed(scId);
                          }}
                          className="flex-1 text-xs px-2.5 py-1.5 bg-white border border-slate-200 rounded-lg font-medium text-slate-700 outline-none focus:ring-1 focus:ring-[#0094c8] cursor-pointer"
                        >
                          <option value="">+ Assign additional school to oversee...</option>
                          {unassignedSchoolsForServed.map(s => (
                            <option key={s.id} value={s.id}>{s.name}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )}

                  {/* Student Secondary / Career Tech Center Schools */}
                  {isStudent && (
                    <>
                      <div className="flex items-center justify-between px-5 py-2 hover:bg-slate-50/50">
                        <span className="text-slate-500 font-medium">Date of Birth & Age</span>
                        <div className="flex items-center gap-2">
                          <input
                            type="date"
                            value={dobDraft}
                            onChange={(e) => setDobDraft(e.target.value)}
                            onBlur={handleSaveDob}
                            className="text-xs px-2 py-1 bg-white border border-slate-200 rounded font-semibold text-slate-800 focus:ring-1 focus:ring-[#0094c8] outline-none"
                          />
                          {age !== null ? (
                            <span className={`font-black px-1.5 py-0.5 rounded text-[10px] ${
                              isUnder18 
                                ? 'text-amber-700 bg-amber-50 border border-amber-200' 
                                : 'text-emerald-700 bg-emerald-50 border border-emerald-200'
                            }`}>
                              {age} yrs ({isUnder18 ? 'Minor' : 'Adult 18+'})
                            </span>
                          ) : (
                            <span className="text-slate-400 text-[10px]">—</span>
                          )}
                        </div>
                      </div>

                      {/* Secondary / Career Center Assignment */}
                      <div className="px-5 py-2.5 bg-slate-50/40 space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-slate-600 font-bold text-xs flex items-center gap-1.5">
                            <GraduationCap className="w-3.5 h-3.5 text-[#0094c8]" />
                            <span>Career Center / Secondary Campuses</span>
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {additionalSchoolsDraft.map((schoolName, idx) => (
                            <span
                              key={idx}
                              className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-lg text-xs font-semibold bg-sky-50 text-sky-800 border border-sky-200"
                            >
                              <span>{schoolName}</span>
                              <button
                                type="button"
                                onClick={() => handleRemoveAdditionalSchool(schoolName)}
                                className="text-slate-400 hover:text-rose-600 rounded p-0.5"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </span>
                          ))}
                          {additionalSchoolsDraft.length === 0 && (
                            <span className="text-slate-400 italic text-[11px]">No secondary campuses assigned.</span>
                          )}
                        </div>
                        <div className="pt-1">
                          <select
                            value={selectedAdditionalSchoolToAdd}
                            onChange={(e) => {
                              const scId = e.target.value;
                              setSelectedAdditionalSchoolToAdd(scId);
                              if (scId) handleAddAdditionalSchool(scId);
                            }}
                            className="w-full text-xs px-2.5 py-1 bg-white border border-slate-200 rounded-lg text-slate-700 cursor-pointer"
                          >
                            <option value="">+ Assign Career Center / Tech Campus...</option>
                            {unassignedSchoolsForStudent.map(s => (
                              <option key={s.id} value={s.id}>{s.name}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Section: FERPA Compliance & Statutory Attestation Log (Students & Parents) */}
          {(isStudent || isParent) && (
            <div>
              <div className="px-5 py-2 bg-slate-50 text-[10px] font-black uppercase tracking-wider text-slate-500 flex items-center justify-between border-b border-slate-100">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="w-3.5 h-3.5 text-[#0094c8]" />
                  <span>FERPA Statutory Consent & Attestation Log</span>
                </div>
                <button
                  type="button"
                  onClick={() => setShowFerpaAuditDetails(!showFerpaAuditDetails)}
                  className="text-[9px] font-bold text-[#0094c8] hover:text-[#007ba6] flex items-center gap-1 cursor-pointer transition-colors"
                >
                  <FileText className="w-3 h-3" />
                  <span>{showFerpaAuditDetails ? 'Hide Legal Certificates' : 'View Attestation Certificates & Audit Trail'}</span>
                  {showFerpaAuditDetails ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                </button>
              </div>

              <div className="divide-y divide-slate-100">
                {/* FERPA Status */}
                <div className="flex items-center justify-between px-5 py-2 hover:bg-slate-50/50">
                  <span className="text-slate-500 font-medium">FERPA Status</span>
                  {ferpaGranted ? (
                    <span className="inline-flex items-center gap-1.5 text-emerald-700 font-bold">
                      <ShieldCheck className="w-4 h-4 text-emerald-600" />
                      <span>Granted & Active (Attested)</span>
                    </span>
                  ) : ferpaSentAt || ferpaRecipient ? (
                    <span className="inline-flex items-center gap-1.5 text-amber-700 font-bold">
                      <Clock className="w-4 h-4 text-amber-600" />
                      <span>Consent Sent (Awaiting Parent Attestation)</span>
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 text-slate-400 font-bold">
                      <AlertTriangle className="w-4 h-4 text-slate-400" />
                      <span>Pending Authorization</span>
                    </span>
                  )}
                </div>

                {/* Signatory Name */}
                <div className="flex items-center justify-between px-5 py-2 hover:bg-slate-50/50">
                  <span className="text-slate-500 font-medium">Signatory Name</span>
                  <span className="font-semibold text-slate-800">{ferpaSignatory || '—'}</span>
                </div>

                {/* Authorization Date */}
                <div className="flex items-center justify-between px-5 py-2 hover:bg-slate-50/50">
                  <span className="text-slate-500 font-medium">Authorization Date</span>
                  <span className="font-semibold text-slate-800">{ferpaConsentDate ? formatDateTime(ferpaConsentDate) : '—'}</span>
                </div>

                {/* Student Legal Attestation Status */}
                {isStudent && (
                  <div className="flex items-center justify-between px-5 py-2 hover:bg-slate-50/50">
                    <span className="text-slate-500 font-medium">Student Legal Attestation</span>
                    {studentAttestation?.certified ? (
                      <span className="inline-flex items-center gap-1.5 text-emerald-700 font-bold text-xs">
                        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                        <span>Certified (Disciplinary Liability Shifted)</span>
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-slate-500 font-medium text-xs">
                        <span>Certified at Parent Email Submission</span>
                      </span>
                    )}
                  </div>
                )}

                {/* Parent Legal Attestation Status */}
                <div className="flex items-center justify-between px-5 py-2 hover:bg-slate-50/50">
                  <span className="text-slate-500 font-medium">Parent Perjury Attestation</span>
                  {parentAttestation?.certified ? (
                    <span className="inline-flex items-center gap-1.5 text-emerald-700 font-bold text-xs">
                      <Shield className="w-3.5 h-3.5 text-emerald-600" />
                      <span>Executed Under Penalty of Perjury (34 CFR § 99.30)</span>
                    </span>
                  ) : ferpaGranted ? (
                    <span className="inline-flex items-center gap-1.5 text-emerald-700 font-bold text-xs">
                      <Check className="w-3.5 h-3.5 text-emerald-600" />
                      <span>Authorized via Verified Parent Profile</span>
                    </span>
                  ) : (
                    <span className="text-slate-400 text-xs">Awaiting Parent Signing Certification</span>
                  )}
                </div>

                {/* Parent / Signer Email */}
                {isStudent && (
                  <div className="flex items-center justify-between px-5 py-2 hover:bg-slate-50/50">
                    <span className="text-slate-500 font-medium">Parent / Signer Email</span>
                    <div className="flex items-center gap-1.5">
                      <input
                        type="email"
                        value={recipientEmailDraft}
                        onChange={(e) => setRecipientEmailDraft(e.target.value)}
                        placeholder="parent@email.com"
                        className="text-xs px-2 py-1 bg-white border border-slate-200 rounded font-semibold text-slate-800 w-44 focus:ring-1 focus:ring-[#0094c8] outline-none"
                      />
                      <button
                        type="button"
                        onClick={handleSaveRecipientEmail}
                        className="px-2 py-1 rounded bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-[10px] cursor-pointer"
                        title="Save Email Address"
                      >
                        Save
                      </button>
                      <button
                        type="button"
                        disabled={resending}
                        onClick={handleTriggerResend}
                        className="px-2 py-1 rounded bg-amber-50 hover:bg-amber-100 text-amber-800 border border-amber-200 font-bold text-[10px] cursor-pointer"
                        title="Resend FERPA Consent Email"
                      >
                        {resending ? 'Sending...' : 'Resend'}
                      </button>
                      <button
                        type="button"
                        onClick={async () => {
                          const eighteenYearsAgo = new Date();
                          eighteenYearsAgo.setFullYear(eighteenYearsAgo.getFullYear() - 18);
                          const dobString = eighteenYearsAgo.toISOString().split('T')[0];
                          try {
                            await onUpdateUser(uidStr, {
                              dateOfBirth: dobString,
                              isAdult: true,
                              ferpa18TransitionProcessed: false,
                              parentOversightPreferenceSet: false,
                              allowParentOversightAt18: true
                            });
                            showToast({
                              title: "Simulated 18th Birthday",
                              message: "Birth date set to 18 years ago today. FERPA rights transferred to adult self-consent.",
                              type: "success"
                            });
                          } catch (err: any) {
                            showToast({ title: "Simulation Failed", message: err.message, type: "error" });
                          }
                        }}
                        className="px-2 py-1 rounded bg-[#0094c8]/10 hover:bg-[#0094c8]/20 text-[#0094c8] border border-[#0094c8]/30 font-bold text-[10px] cursor-pointer flex items-center gap-1"
                        title="Set birth date to 18 years ago and trigger adult FERPA self-consent flow"
                      >
                        <Cake className="w-3 h-3" />
                        <span>Simulate 18th Bday</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Expandable Statutory Attestation Dossier & Audit Log */}
              {showFerpaAuditDetails && (
                <div className="p-4 bg-slate-50/90 border-t border-slate-200 space-y-3.5 animate-fade-in text-xs">
                  {/* Card 1: Student Legal Attestation Certificate */}
                  <div className="p-3.5 bg-white border border-amber-200 rounded-xl space-y-2 shadow-2xs">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-black uppercase tracking-wider text-amber-900 bg-amber-100 px-2 py-0.5 rounded">
                        1. Student Legal Attestation (E-SIGN / UETA)
                      </span>
                      <span className="text-[10px] font-mono text-slate-500">
                        {studentAttestation?.timestamp ? formatDateTime(studentAttestation.timestamp) : (user.createdAt ? formatDateTime(user.createdAt) : 'Recorded on account creation')}
                      </span>
                    </div>
                    <div className="p-2.5 bg-amber-50/60 rounded-lg border border-amber-200/60 text-slate-800 italic leading-relaxed">
                      "{studentAttestation?.statement || 'I certify under penalty of disciplinary action, loss of program credit, and potential removal from the Work-Based Learning program that the email address provided belongs exclusively to my legal parent or court-appointed legal guardian.'}"
                    </div>
                    <div className="flex flex-wrap items-center justify-between text-[11px] text-slate-600 pt-1">
                      <span><strong>Recipient Email Bound:</strong> {studentAttestation?.emailProvided || user.parentEmail || recipientEmailDraft || 'Recorded in profile'}</span>
                      <span className="text-emerald-700 font-bold">✓ Affirmative Sign-off Required Before Dispatch</span>
                    </div>
                  </div>

                  {/* Card 2: Parent/Guardian Legal Attestation Certificate */}
                  <div className="p-3.5 bg-white border border-emerald-200 rounded-xl space-y-2 shadow-2xs">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-black uppercase tracking-wider text-emerald-900 bg-emerald-100 px-2 py-0.5 rounded">
                        2. Parent/Guardian Statutory Certification (34 CFR § 99.30)
                      </span>
                      <span className="text-[10px] font-mono text-slate-500">
                        {parentAttestation?.timestamp ? formatDateTime(parentAttestation.timestamp) : (ferpaConsentDate ? formatDateTime(ferpaConsentDate) : 'Pending Execution')}
                      </span>
                    </div>
                    <div className="p-2.5 bg-emerald-50/60 rounded-lg border border-emerald-200/60 text-slate-800 italic leading-relaxed">
                      "{parentAttestation?.statement || 'I certify under penalty of perjury and law that I am the legal parent or court-appointed legal guardian of the minor student named herein and possess full legal authority to grant educational consent under FERPA (34 CFR § 99.30).'}"
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-[11px] text-slate-600 pt-1">
                      <div>
                        <strong>Signer Identity:</strong> {parentAttestation?.signerName || ferpaSignatory || 'Pending'} ({parentAttestation?.signerEmail || ferpaRecipient || 'Pending'})
                      </div>
                      <div>
                        <strong>IP Address:</strong> <span className="font-mono text-slate-800">{parentAttestation?.ipAddress || 'Verified electronic session IP recorded'}</span>
                      </div>
                      <div className="sm:col-span-2 truncate">
                        <strong>Device / User-Agent:</strong> <span className="font-mono text-slate-500 text-[10px]">{parentAttestation?.userAgent || 'Verified client browser environment'}</span>
                      </div>
                    </div>
                  </div>

                  {/* Card 3: Vendor Protection & District License Agreement Notice */}
                  <div className="p-3 bg-blue-50/70 border border-blue-200 rounded-xl space-y-1.5 text-slate-700">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-black uppercase tracking-wider text-blue-900 flex items-center gap-1.5">
                        <Scale className="w-3.5 h-3.5 text-blue-700" />
                        3. Platform Terms of Service & District License Agreement (§ 4.2)
                      </span>
                      <span className="text-[9px] font-mono text-blue-800 font-bold">LosoNova LLC Protected</span>
                    </div>
                    <p className="text-[11px] text-blue-950 leading-relaxed">
                      <strong>Accuracy Disclaimer:</strong> LosoNova LLC provides electronic delivery software and relies strictly on the affirmative legal certifications and attestations made by user signers pursuant to E-SIGN (15 U.S.C. § 7001) and South Carolina UETA (S.C. Code Ann. § 26-6-10).
                    </p>
                    <p className="text-[11px] text-blue-950 leading-relaxed">
                      <strong>No Independent Audit Obligation:</strong> The platform does not independently verify familial or legal guardianship relationships. Liability for fraudulent or incorrect information submitted by students rests solely on the individual making the false representation, holding school districts and LosoNova LLC harmless.
                    </p>
                  </div>

                  {/* Card 4: Historical FERPA Audit Log Ledger */}
                  <div className="p-3.5 bg-white border border-slate-200 rounded-xl space-y-2 shadow-2xs">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-black uppercase tracking-wider text-slate-700 flex items-center gap-1.5">
                        <Clock className="w-3.5 h-3.5 text-slate-500" />
                        Historical System Audit Events ({ferpaAuditLogs.length})
                      </span>
                      <span className="text-[9px] font-mono text-slate-400">Firestore `logs` collection</span>
                    </div>

                    {ferpaAuditLogs.length > 0 ? (
                      <div className="space-y-2 max-h-48 overflow-y-auto divide-y divide-slate-100 pr-1">
                        {ferpaAuditLogs.map((log) => (
                          <div key={log.id} className="pt-2 first:pt-0 space-y-0.5">
                            <div className="flex items-center justify-between text-[10px]">
                              <span className="font-bold text-slate-800 bg-slate-100 px-1.5 py-0.5 rounded font-mono">
                                {log.action}
                              </span>
                              <span className="text-slate-400 font-mono">
                                {formatDateTime(log.timestamp)}
                              </span>
                            </div>
                            <p className="text-[11px] text-slate-600 leading-snug">
                              {log.details}
                            </p>
                            <span className="text-[10px] text-slate-400 block">
                              Actor: {log.userEmail}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-[11px] text-slate-500 italic py-1">
                        Live snapshot observer active. All student attestations, parent perjury certifications, and automated consent notifications are recorded to this immutable audit ledger.
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Section: Linked Relationships */}
          <div>
            <div className="px-5 py-1.5 bg-slate-50 text-[10px] font-black uppercase tracking-wider text-slate-400">
              Relationships & Contacts
            </div>
            <div className="divide-y divide-slate-100">
              {isStudent && (
                <>
                  <div className="flex items-center justify-between px-5 py-2 hover:bg-slate-50/50">
                    <span className="text-slate-500 font-medium">Linked Parent(s)</span>
                    <div className="text-right">
                      {linkedParents.length > 0 ? (
                        linkedParents.map(p => (
                          <button
                            key={p.uid}
                            type="button"
                            onClick={() => onSelectAnotherUser?.(p)}
                            className="text-[#0094c8] hover:underline font-bold block"
                          >
                            {p.name} ({p.email})
                          </button>
                        ))
                      ) : (
                        <span className="text-slate-400">None linked</span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center justify-between px-5 py-2 hover:bg-slate-50/50">
                    <span className="text-slate-500 font-medium">School Advisor(s)</span>
                    <div className="text-right">
                      {assignedAdvisors.length > 0 ? (
                        assignedAdvisors.map(a => (
                          <span key={a.uid} className="font-semibold text-slate-700 block">
                            {a.name} ({a.email})
                          </span>
                        ))
                      ) : (
                        <span className="text-slate-400">None assigned</span>
                      )}
                    </div>
                  </div>
                </>
              )}

              {isParent && (
                <div className="flex items-center justify-between px-5 py-2 hover:bg-slate-50/50">
                  <span className="text-slate-500 font-medium">Linked Student(s)</span>
                  <div className="text-right">
                    {linkedStudents.length > 0 ? (
                      linkedStudents.map(s => (
                        <button
                          key={s.uid}
                          type="button"
                          onClick={() => onSelectAnotherUser?.(s)}
                          className="text-[#0094c8] hover:underline font-bold block"
                        >
                          {s.name} ({s.school || 'Campus'})
                        </button>
                      ))
                    ) : (
                      <span className="text-slate-400">None linked</span>
                    )}
                  </div>
                </div>
              )}

              {isStaffOrAdmin && (
                <div className="flex items-center justify-between px-5 py-2 hover:bg-slate-50/50">
                  <span className="text-slate-500 font-medium">Summary Coverage</span>
                  <span className="font-semibold text-slate-700 text-right max-w-[280px] truncate">
                    {[user.school, ...(schoolsServedDraft || [])].filter(Boolean).join(', ') || 'None assigned'}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Section: System Metadata & Audit */}
          <div>
            <div className="px-5 py-1.5 bg-slate-50 text-[10px] font-black uppercase tracking-wider text-slate-400 flex items-center justify-between">
              <span>System Audit</span>
              {isSuperadmin && (
                <button
                  type="button"
                  onClick={() => setIsEditingAuditDates(!isEditingAuditDates)}
                  className="text-[9px] font-bold text-[#0094c8] hover:underline flex items-center gap-1 cursor-pointer"
                >
                  <Edit2 className="w-2.5 h-2.5" />
                  <span>{isEditingAuditDates ? 'Done Adjusting' : 'Adjust Timestamps'}</span>
                </button>
              )}
            </div>
            <div className="divide-y divide-slate-100">
              {/* Account Created */}
              <div className="flex items-center justify-between px-5 py-2.5 hover:bg-slate-50/50">
                <span className="text-slate-500 font-medium">Account Created</span>
                {isEditingAuditDates ? (
                  <input
                    type="datetime-local"
                    defaultValue={toDatetimeLocalValue(createdAtDraft || fallbackCreatedAt)}
                    onBlur={(e) => handleSaveAuditDate('createdAt', e.target.value)}
                    className="text-xs px-2 py-1 bg-white border border-slate-300 rounded font-semibold text-slate-800 outline-none focus:ring-1 focus:ring-[#0094c8]"
                  />
                ) : (
                  <span className="font-semibold text-slate-800 font-mono text-[11px]">
                    {formatDateTime(createdAtDraft || fallbackCreatedAt)}
                  </span>
                )}
              </div>

              {/* Last Login / Active */}
              <div className="flex items-center justify-between px-5 py-2.5 hover:bg-slate-50/50">
                <span className="text-slate-500 font-medium">Last Login / Active</span>
                {isEditingAuditDates ? (
                  <input
                    type="datetime-local"
                    defaultValue={toDatetimeLocalValue(lastLoginDraft || fallbackLastLogin)}
                    onBlur={(e) => handleSaveAuditDate('lastLogin', e.target.value)}
                    className="text-xs px-2 py-1 bg-white border border-slate-300 rounded font-semibold text-slate-800 outline-none focus:ring-1 focus:ring-[#0094c8]"
                  />
                ) : (
                  <span className="font-semibold text-slate-800 font-mono text-[11px]">
                    {formatDateTime(lastLoginDraft || fallbackLastLogin)}
                  </span>
                )}
              </div>
            </div>
          </div>

        </div>

        {/* Footer */}
        <div className="px-5 py-3 bg-slate-50 border-t border-slate-100 flex items-center justify-between">
          <div className="text-[10px] text-slate-400 font-medium">
            Changes to editable fields save automatically on blur or selection.
          </div>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-1.5 rounded-lg text-xs font-black uppercase tracking-wider bg-slate-200 hover:bg-slate-300 text-slate-700 transition-all cursor-pointer"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
